import { ReactNode } from 'react';

import { PlanCode } from '@src/helpers/plan-constants.helper';
import { AffiliateCode } from '@src/constants/affiliate';

import { Quote } from '@src/@types/quotes';
import { PetAgeRange, PetProfile } from '@src/@types/pets';
import { UserProfile } from '@src/@types/users';
import { UserClinic } from '@src/@types/clinics';
import { UserAddress } from '@src/@types/addresses';
import { PaymentDetails } from '@src/@types/payments';
import { PaymentProcessor } from '@src/@types/payment-processors';
import { PolicyResponse } from '@src/@types/policies';
import { PolicyExternalLinks } from '@src/pages/policies/show/components/policy-external-links.type';

import { UserIdContext } from '@src/contexts/user-id-context';
import { SelfContext } from '@src/contexts/self-context';
import { UserContext } from '@src/contexts/user-context';
import { PetIneligibleReasonsContext } from '@src/contexts/pet-ineligible-reasons-context';
import { PolicyContext } from '@src/contexts/policy-context';
import { ClinicsContext } from '@src/contexts/clinics-context';
import { PaymentsContext } from '@src/contexts/payments-context';
import { AddressesContext } from '@src/contexts/addresses-context';
import { OtpRequiredContext } from '@src/contexts/otp-required-context';
import { AllPetProfilesContext } from '@src/contexts/all-pet-profiles-context';
import { PaymentProcessorContext } from '@src/contexts/payment-processor-context';
import { CurrentPetProfileContext } from '@src/contexts/current-pet-profile-context';
import { PolicyExternalLinksContext } from '@src/contexts/policy-external-links-context';
import { SMSConsentDataContext } from '@src/contexts/sms-consent-data-context';
import { SMSConsentData } from '@src/@types/sms';
import { PolicyModificationOptionsContext } from '@src/contexts/policy-modification-options-context';
import { AuthenticationStateContext } from '@src/contexts/authentication-state-context';
import { QuoteContextProvider } from '@src/contexts/providers/quote-provider';
import { CommunicationEmailDataContext } from '@src/contexts/communication-email-data-context';
import { AffiliateContext } from '@src/contexts/affiliate.context';

export const PageContextProvider = ({
  children,
  pageContextData,
}: {
  children: ReactNode;
  pageContextData: Record<string, string> | undefined;
}) => {
  const petProfiles = getParsedProperty<PetProfile[]>(
    pageContextData,
    'pets',
    [],
  );
  const currentPet = {
    ...getParsedProperty(pageContextData, 'pet', {} as PetProfile),
    petAgeRange:
      PetAgeRange[
        getParsedProperty(
          pageContextData,
          'petAge',
          '' as keyof typeof PetAgeRange,
        )
      ],
  } as PetProfile;

  const addresses = getParsedProperty<UserAddress[]>(
    pageContextData,
    'addresses',
    [],
  );
  const clinics = getParsedProperty<UserClinic[]>(
    pageContextData,
    'clinics',
    [],
  );
  const quote = getParsedProperty(pageContextData, 'quote', {} as Quote);
  const petIneligibilityReasons = getParsedProperty(
    pageContextData,
    'petIneligibilityMap',
    {} as Record<string, string>,
  );
  const authenticationState = getParsedProperty(
    pageContextData,
    'authenticationState',
    {} as Record<string, string>,
  );
  const paymentMethods = getParsedProperty<PaymentDetails[]>(
    pageContextData,
    'payments',
    [],
  );
  const paymentProcessor = getParsedProperty(
    pageContextData,
    'paymentprocessor',
    {} as PaymentProcessor,
  );
  const user = getParsedProperty(pageContextData, 'user', {} as UserProfile);
  const self = getParsedProperty(pageContextData, 'self', {} as UserAddress);
  const smsConsent = getParsedProperty(
    pageContextData,
    'smsConsent',
    {} as SMSConsentData,
  );
  const shouldCollectCommunicationEmail = getParsedProperty<boolean>(
    pageContextData,
    'shouldCollectCommunicationEmail',
    false,
  );
  const policy = getParsedProperty<PolicyResponse>(
    pageContextData,
    'policy',
    null,
  );
  const links = getParsedProperty<PolicyExternalLinks>(
    pageContextData,
    'links',
    null,
  );
  const userId = getParsedProperty<number>(pageContextData, 'userId', null);
  const otpRequired = getParsedProperty(pageContextData, 'otp', false);
  const policyModificationOptions = getParsedProperty(
    pageContextData,
    'policyModificationOptions',
    [],
  );
  const affiliateData = {
    batchId: pageContextData?.affiliatesBatchId ?? '',
    showExistingPetModal: getParsedProperty(
      pageContextData,
      'affiliatesShouldShowExistingPetModal',
      false,
    ),
    planCode: (pageContextData?.affiliatesPlanCode as PlanCode) ?? null,
    affiliateCode: (pageContextData?.affiliatesCode as AffiliateCode) ?? null,
    affiliatePet: {
      ...getParsedProperty(pageContextData, 'affiliatesPet', {} as PetProfile),
      petAgeRange:
        PetAgeRange[
          getParsedProperty(
            pageContextData,
            'petAge',
            '' as keyof typeof PetAgeRange,
          )
        ],
    } as PetProfile,
  };

  return (
    <AllPetProfilesContext.Provider value={petProfiles}>
      <CurrentPetProfileContext.Provider value={currentPet}>
        <AffiliateContext.Provider value={affiliateData}>
          <AddressesContext.Provider value={addresses}>
            <ClinicsContext.Provider value={clinics}>
              <QuoteContextProvider value={quote}>
                <PetIneligibleReasonsContext.Provider
                  value={petIneligibilityReasons}
                >
                  <AuthenticationStateContext.Provider
                    value={authenticationState}
                  >
                    <PaymentsContext.Provider value={paymentMethods}>
                      <PaymentProcessorContext.Provider
                        value={paymentProcessor}
                      >
                        <UserContext.Provider value={user}>
                          <SelfContext.Provider value={self}>
                            <PolicyContext.Provider value={policy}>
                              <PolicyExternalLinksContext.Provider
                                value={links}
                              >
                                <OtpRequiredContext.Provider
                                  value={otpRequired}
                                >
                                  <UserIdContext.Provider value={userId}>
                                    <SMSConsentDataContext.Provider
                                      value={smsConsent}
                                    >
                                      <CommunicationEmailDataContext.Provider
                                        value={shouldCollectCommunicationEmail}
                                      >
                                        <PolicyModificationOptionsContext.Provider
                                          value={policyModificationOptions}
                                        >
                                          {children}
                                        </PolicyModificationOptionsContext.Provider>
                                      </CommunicationEmailDataContext.Provider>
                                    </SMSConsentDataContext.Provider>
                                  </UserIdContext.Provider>
                                </OtpRequiredContext.Provider>
                              </PolicyExternalLinksContext.Provider>
                            </PolicyContext.Provider>
                          </SelfContext.Provider>
                        </UserContext.Provider>
                      </PaymentProcessorContext.Provider>
                    </PaymentsContext.Provider>
                  </AuthenticationStateContext.Provider>
                </PetIneligibleReasonsContext.Provider>
              </QuoteContextProvider>
            </ClinicsContext.Provider>
          </AddressesContext.Provider>
        </AffiliateContext.Provider>
      </CurrentPetProfileContext.Provider>
    </AllPetProfilesContext.Provider>
  );
};

// determine if a property exists on the context value, and if it does, return
// the value of that property passed through JSON.parse, or return the default
// value
export const getParsedProperty = <T = unknown,>(
  contextValue: Record<string, string> | undefined,
  property: string,
  defaultValue: T,
): T => {
  if (contextValue && contextValue[property]) {
    try {
      return JSON.parse(contextValue[property]) ?? defaultValue;
    } catch (e) {
      // don't break the page if the context value is somehow invalid JSON
    }
  }
  return defaultValue;
};
